import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

import { getHandoffToken } from '../core/preauth';
import { getSessionSource, notifySessionChange } from '../core/sessionState';
import { currentLoadPhases, recordPhase, recordPhaseMax } from './loadPhases';

type ParsedJwt = { exp: number; [key: string]: unknown };

export const parseJwt = (token: string): ParsedJwt => {
  const base64Url = token.split('.')[1] ?? '';
  const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
  return JSON.parse(window.atob(base64));
};

export const isExpired = (jwt: ParsedJwt | null): boolean => {
  if (!jwt) return true;
  const now = Date.now();
  const refreshWindowMs = 10 * 60 * 1000;
  return jwt.exp * 1000 <= now + refreshWindowMs;
};

export const clearSession = (): void => {
  localStorage.clear();
  sessionStorage.clear();
};

const NO_USER_EXCEPTION = 'UserUnAuthenticatedException';
const SESSION_LOST = 'InsightSessionLost';

const isNoUser = (error: unknown): boolean => (error as { name?: string } | null)?.name === NO_USER_EXCEPTION;

const isSessionLoss = (error: unknown): boolean => {
  const name = (error as { name?: string } | null)?.name;
  return name === NO_USER_EXCEPTION || name === SESSION_LOST;
};

export const hasInsightSession = async (): Promise<boolean> => {
  try {
    await getCurrentUser();
    return true;
  } catch (error) {
    return !isNoUser(error);
  }
};

const noSession = () => Object.assign(new Error('No active session'), { name: SESSION_LOST });

export const getAmplifyIdToken = async (): Promise<string | null> => {
  const recorder = currentLoadPhases();
  const started = performance.now();
  try {
    if (!(await hasInsightSession())) return null;
    let { tokens } = await fetchAuthSession();
    let jwt = tokens?.idToken?.toString();
    if (jwt && isExpired(parseJwt(jwt))) {
      ({ tokens } = await fetchAuthSession({ forceRefresh: true }));
      jwt = tokens?.idToken?.toString();
    }
    if (!jwt) throw noSession();
    return jwt;
  } finally {
    const ms = performance.now() - started;
    recordPhase('token', ms, recorder);
    recordPhaseMax('tokenMax', ms, recorder);
  }
};

const insightTokenOrClose = async (): Promise<string> => {
  let own: string | null;
  try {
    own = await getAmplifyIdToken();
  } catch (error) {
    if (isSessionLoss(error)) notifySessionChange('lost', 'amplify');
    throw error;
  }
  if (own) return own;
  notifySessionChange('lost', 'amplify');
  throw noSession();
};

const handoffTokenOrClose = (): string => {
  const token = getHandoffToken();
  if (token) return token;
  notifySessionChange('lost', 'handoff');
  throw noSession();
};

export const getJWTToken = async (): Promise<string> => {
  const source = getSessionSource();
  if (source === 'handoff') return handoffTokenOrClose();
  if (source === 'amplify') return insightTokenOrClose();
  const handoff = getHandoffToken();
  if (handoff) return handoff;
  const own = await getAmplifyIdToken();
  if (own) return own;
  throw noSession();
};
